import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserRole } from '../enums/role-name';
import { Employment } from '../types/employment';
import { User } from '../types/user';

@Injectable({
  providedIn: 'root',
})
export class TeamMemberService {
  private _numberOfTeamMembers$: BehaviorSubject<number> = new BehaviorSubject(0);

  get numberOfTeamMembers$(): Observable<number> {
    return this._numberOfTeamMembers$.asObservable();
  }

  constructor(private http: HttpClient) {}

  getTeamMembers(enterpriseId: string, facilityId: string): Observable<User[] | undefined> {
    return this.http.get<User[]>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/facilities/${facilityId}/team-members`,
    );
  }

  getEnterpriseTeamMembers(enterpriseId: string): Observable<User[]> {
    const allRoles = Object.values(UserRole);
    return this.getTeamMembersByRoles(enterpriseId, allRoles);
  }

  getTeamMembersByRoles(enterpriseId: string, roles: string[]): Observable<User[]> {
    return this.http.get<User[]>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/team-members?roles=${roles.join(',')}`,
    );
  }

  getTeamMember(enterpriseId: string, facilityId: string, employmentId: string): Observable<User> {
    return this.http.get<User>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/facilities/${facilityId}/team-members/${employmentId}`,
    );
  }

  getNumberOfTeamMembers(enterpriseId: string): void {
    this.http
      .get<any>(environment.apiDomain + `/auth/enterprises/${enterpriseId}/team-members/size`)
      .toPromise()
      .then(
        (result) => this._numberOfTeamMembers$.next(result.numberOfEmployees),
        (_) => this._numberOfTeamMembers$.next(0),
      );
  }

  addTeamMember(enterpriseId: string, facilityId: string, teamMember: any): Observable<void> {
    return this.http.post<void>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/facilities/${facilityId}/team-members`,
      teamMember,
    );
  }

  changeTeamMember(enterpriseId: string, facilityId: string, teamMember: any): Observable<void> {
    return this.http.put<void>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/facilities/${facilityId}/team-members`,
      teamMember,
    );
  }

  deleteTeamMember(enterpriseId: string, facilityId: string, employmentId: string): Observable<void> {
    return this.http.delete<void>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/facilities/${facilityId}/team-members/${employmentId}`,
    );
  }

  isEnterpriseAdminOrAccountOwner(enterpriseId: string): Observable<any> {
    return this.http.get<any>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/is-enterprise-admin-account-owner`,
    );
  }

  getWorksiteAdmins(enterpriseId: string): Observable<User[]> {
    return this.getTeamMembersByRoles(enterpriseId, [
      UserRole.ACCOUNT_OWNER,
      UserRole.ENTERPRISE_ADMIN,
      UserRole.WORKSITE_ADMIN,
    ]);
  }

  getEnterpriseAdmins(enterpriseId: string): Observable<User[]> {
    return this.getTeamMembersByRoles(enterpriseId, [UserRole.ACCOUNT_OWNER, UserRole.ENTERPRISE_ADMIN]);
  }

  getAccountOwners(enterpriseId: string): Observable<User[]> {
    return this.getTeamMembersByRoles(enterpriseId, [UserRole.ACCOUNT_OWNER]);
  }

  getEmployment(user: User, enterpriseId: string): Employment {
    return user.employments.filter((e) => e.enterpriseId === enterpriseId)[0];
  }

  getDefaultFacilityId(user: User, enterpriseId: string): string {
    let employment = this.getEmployment(user, enterpriseId);
    return employment.facilityEmployments[0].facilityId;
  }

  resendTeamMemberInvitation(enterpriseId: string, userId: string, teamMember: any): Observable<void> {
    return this.http.post<void>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/users/${userId}/resend-team-member-invitation`,
      teamMember,
    );
  }

  getUsersByFacilityId(enterpriseId: string, facilityId: string): Observable<User[]> {
    return this.http.get<User[]>(
      environment.apiDomain + `/auth/enterprises/${enterpriseId}/facilities/${facilityId}/users`,
    );
  }
}
